import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Member } from "../../domain/entities/Member";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { CheckCircle, MoreVertical } from "lucide-react";
import { ViewMemberDialog } from "./ViewMemberDialog";
import InActiveIcon from "@/assets/icons/inactive.svg?react";
import EditIcon from "@/assets/icons/edit.svg?react";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import { PermissionGuard } from "@/components/PermissionGuard";
import ExtraPermissionsIcon from "@/assets/icons/extra-permissions.svg?react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  classicTableOuterClass,
  classicTableScrollClass,
  classicStickyTableHeaderClass,
} from "@/components/layout/tableLayout";

interface MembersTableProps {
  members: Member[];
  isLoading?: boolean;
  onView: (member: Member) => void;
  onEdit: (member: Member) => void;
  onRemove: (member: Member) => void;
  onMarkInactive: (member: Member) => void;
  onMarkActive: (member: Member) => void;
  onExtraPermissions: (member: Member) => void;
  canView?: boolean;
  canEdit?: boolean;
  canMarkInactive?: boolean;
  canMarkActive?: boolean;
  canRemove?: boolean;
  currentUserId?: string;
}

export const MembersTable: React.FC<MembersTableProps> = ({
  members,
  isLoading,
  onView,
  onEdit,
  onRemove,
  onMarkInactive,
  onMarkActive,
  onExtraPermissions,
  canView = true,
  canEdit = true,
  canMarkInactive = true,
  canMarkActive = true,
  canRemove = true,
  currentUserId,
}) => {
  const { t } = useTranslation("membersGroups");
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [openView, setOpenView] = useState(false);

  const handleViewClick = (member: Member) => {
    setSelectedMember(member);
    setOpenView(true);
    onView(member);
  };

  const handleRowClick = (member: Member) => {
    if (!canView) return;
    handleViewClick(member);
  };

  const stopRowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const isInstituteAdminMember = (member: Member) =>
    member.role.replace(/[\s-]+/g, "_").toUpperCase() === "INSTITUTE_ADMIN";

  const canEditMember = (member: Member) =>
    canEdit && (!isInstituteAdminMember(member) || member.id === currentUserId);

  return (
    <>
      <div className="mt-4 space-y-3 md:hidden">
        {isLoading ? (
          <div className="rounded-xl border bg-white p-6 text-center text-muted-foreground">
            {t("membersTable.loading")}
          </div>
        ) : members.length === 0 ? (
          <div className="rounded-xl border bg-white p-6 text-center text-muted-foreground">
            {t("membersTable.empty")}
          </div>
        ) : (
          members.map((m) => (
            <div
              key={m.id}
              className={`rounded-xl border bg-white p-4 ${
                canView
                  ? "cursor-pointer transition-colors hover:bg-gray-50"
                  : ""
              }`}
              onClick={() => handleRowClick(m)}
              onKeyDown={(event) => {
                if (!canView) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleRowClick(m);
                }
              }}
              role={canView ? "button" : undefined}
              tabIndex={canView ? 0 : undefined}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium text-gray-900">{m.name}</p>
                  <p className="truncate text-sm text-gray-500">{m.email}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="rounded-md p-1"
                      onClick={stopRowClick}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <PermissionGuard allowed={canView}>
                      <DropdownMenuItem onClick={() => handleViewClick(m)}>
                        {t("membersTable.view")}
                      </DropdownMenuItem>
                    </PermissionGuard>
                    <DropdownMenuItem onClick={() => onExtraPermissions(m)}>
                      {t("membersTable.extraPermissions")}
                    </DropdownMenuItem>
                    {m.status !== "Invited" && canEditMember(m) && (
                      <DropdownMenuItem onClick={() => onEdit(m)}>
                        <EditIcon /> {t("membersTable.edit")}
                      </DropdownMenuItem>
                    )}
                    {m.status === "Active" &&
                      !isInstituteAdminMember(m) &&
                      canMarkInactive && (
                      <DropdownMenuItem onClick={() => onMarkInactive(m)}>
                        <InActiveIcon className="h-4 w-4 shrink-0" />
                        <span>{t("membersTable.markInactive")}</span>
                      </DropdownMenuItem>
                    )}
                    {m.status === "Inactive" && canMarkActive && (
                      <DropdownMenuItem onClick={() => onMarkActive(m)}>
                        <CheckCircle className="h-4 w-4 shrink-0" />
                        <span>{t("membersTable.markActive")}</span>
                      </DropdownMenuItem>
                    )}
                    {canRemove && (
                      <DropdownMenuItem
                        onClick={() => onRemove(m)}
                        className="text-destructive-text"
                      >
                        <DeleteIcon /> {t("membersTable.remove")}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs uppercase text-gray-400">{t("membersTable.phone")}</p>
                  <p className="mt-1 text-gray-700">{m.phone}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-400">{t("membersTable.role")}</p>
                  <p className="mt-1 text-gray-700">{m.role}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-400">{t("membersTable.status")}</p>
                  <span
                    className={`mt-1 inline-flex rounded-md px-2 py-1 text-xs font-medium ${
                      m.status === "Active"
                        ? "bg-green-100 text-green-600"
                        : m.status === "Invited"
                          ? "bg-blue-100 text-blue-600"
                          : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {m.status}
                  </span>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-400">{t("membersTable.created")}</p>
                  <p className="mt-1 text-gray-700">{m.createdAt}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className={`${classicTableOuterClass} mt-4`}>
        <div className={classicTableScrollClass}>
          <Table className="w-full min-w-[700px]">
            {/* Header */}
            <TableHeader className={classicStickyTableHeaderClass}>
              <TableRow className="bg-[#D8DDE6] hover:bg-[#D8DDE6]">
                <TableHead className="py-3 px-4 font-bold">{t("membersTable.nameCol")}</TableHead>
                <TableHead className="py-3 px-4 font-bold">{t("membersTable.emailCol")}</TableHead>
                <TableHead className="py-3 px-4 font-bold">{t("membersTable.phoneCol")}</TableHead>
                <TableHead className="py-3 px-4 font-bold">{t("membersTable.roleCol")}</TableHead>
                <TableHead className="py-3 px-4 font-bold">{t("membersTable.statusCol")}</TableHead>
                <TableHead className="py-3 px-4 font-bold">{t("membersTable.createdCol")}</TableHead>
                <TableHead className="py-3 px-4 font-bold text-right">{t("membersTable.actionsCol")}</TableHead>
              </TableRow>
            </TableHeader>

            {/* Body */}
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-8 text-muted-foreground"
                  >
                    {t("membersTable.loading")}
                  </TableCell>
                </TableRow>
              ) : members.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-8 text-muted-foreground"
                  >
                    {t("membersTable.empty")}
                  </TableCell>
                </TableRow>
              ) : (
                members.map((m) => (
                  <TableRow
                    key={m.id}
                    onClick={() => handleRowClick(m)}
                    className={
                      canView ? "cursor-pointer hover:bg-gray-50" : undefined
                    }
                  >
                    <TableCell className="py-3 px-4">{m.name}</TableCell>
                    <TableCell className="py-3 px-4">{m.email}</TableCell>
                    <TableCell className="py-3 px-4">{m.phone}</TableCell>
                    <TableCell className="py-3 px-4">{m.role}</TableCell>

                    {/* Status Badge */}
                    <TableCell className="py-3 px-4">
                      <span
                        className={`
                    px-2 py-1 rounded-md text-xs font-medium
                    ${
                      m.status === "Active"
                        ? "bg-green-100 text-green-600"
                        : m.status === "Invited"
                          ? "bg-blue-100 text-blue-600"
                          : "bg-gray-100 text-gray-500"
                    }
                  `}
                      >
                        {m.status}
                      </span>
                    </TableCell>

                    <TableCell className="py-3 px-4">{m.createdAt}</TableCell>

                    {/* Actions */}
                    <TableCell
                      className="flex items-center justify-end gap-3 py-3 px-4"
                      onClick={stopRowClick}
                    >
                      {/* View */}
                      <PermissionGuard allowed={canView}>
                        <button
                          type="button"
                          className="app-text-button"
                          onClick={() => handleViewClick(m)}
                        >
                          {t("membersTable.view")}
                        </button>
                      </PermissionGuard>

                      {/* 3-dot menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button type="button">
                            <MoreVertical className="h-4 w-4 cursor-pointer" />
                          </button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                          align="end"
                          className="w-40 space-y-2"
                        >
                          {m.status !== "Invited" &&
                            (canEditMember(m) ? (
                              <DropdownMenuItem onClick={() => onEdit(m)}>
                                <EditIcon /> {t("membersTable.edit")}
                              </DropdownMenuItem>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm opacity-50 cursor-not-allowed select-none">
                                    <EditIcon /> {t("membersTable.edit")}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                  {t("membersTable.accessDenied")}
                                </TooltipContent>
                              </Tooltip>
                            ))}

                          <DropdownMenuItem
                            onClick={() => onExtraPermissions(m)}
                          >
                            <ExtraPermissionsIcon className="w-4 h-4 shrink-0" />
                            <span>{t("membersTable.extraPermissions")}</span>
                          </DropdownMenuItem>

                          {m.status === "Active" &&
                            !isInstituteAdminMember(m) &&
                            (canMarkInactive ? (
                              <DropdownMenuItem
                                onClick={() => onMarkInactive(m)}
                              >
                                <InActiveIcon className="w-4 h-4 shrink-0" />
                                <span>{t("membersTable.markInactive")}</span>
                              </DropdownMenuItem>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm opacity-50 cursor-not-allowed select-none">
                                    <InActiveIcon className="w-4 h-4 shrink-0" />
                                    <span>{t("membersTable.markInactive")}</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                  {t("membersTable.accessDenied")}
                                </TooltipContent>
                              </Tooltip>
                            ))}

                          {m.status === "Inactive" &&
                            (canMarkActive ? (
                              <DropdownMenuItem
                                onClick={() => onMarkActive(m)}
                              >
                                <CheckCircle className="w-4 h-4 shrink-0" />
                                <span>{t("membersTable.markActive")}</span>
                              </DropdownMenuItem>
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm opacity-50 cursor-not-allowed select-none">
                                    <CheckCircle className="w-4 h-4 shrink-0" />
                                    <span>{t("membersTable.markActive")}</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                  {t("membersTable.accessDenied")}
                                </TooltipContent>
                              </Tooltip>
                            ))}

                          {canRemove ? (
                            <DropdownMenuItem
                              onClick={() => onRemove(m)}
                              className="text-destructive-text hover:text-destructive-text hover:bg-white"
                            >
                              <DeleteIcon /> {t("membersTable.remove")}
                            </DropdownMenuItem>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm opacity-50 cursor-not-allowed select-none text-destructive-text">
                                  <DeleteIcon /> {t("membersTable.remove")}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="left">
                                {t("membersTable.accessDenied")}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <ViewMemberDialog
        open={openView}
        onOpenChange={setOpenView}
        member={selectedMember}
      />
    </>
  );
};
